/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { AvatarStyles, AvatarVariantOptions } from './themes/AvatarStyles.css';

import { AvatarRedwoodTheme } from './themes/redwood/AvatarTheme';
import { classNames } from '#utils/UNSAFE_classNames';
import { globalImages } from '#ImageVars.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';

type AvatarProps = {
  /**
   * Specifies the source for the image of the avatar.  Will only be displayed if no icon component is specified as a child.  Image will be rendered as a
   * background image.
   */
  src?: string;
  /**
   * Specifies the initials of the avatar.  Will only be displayed if both the src attribute and the child icon component are not specified.
   */
  initials?: string;
  /**
   * Specifies the background of the avatar. The default value of this property is theme driven.
   */
  background?:
    | 'neutral'
    | 'orange'
    | 'green'
    | 'teal'
    | 'blue'
    | 'slate'
    | 'pink'
    | 'purple'
    | 'lilac'
    | 'gray';

  /**
   * Specifies the size of the avatar. The default value of this property is theme driven.
   */
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

  /**
   * Specifies the shape of the avatar. Can be square or circle.  The default value of this
   * property varies by theme.
   */
  shape?: 'square' | 'circle';
  /**
   * Aria-label does not need to be set if associated information is available to assistive technologies elsewhere on the page,
   * for example if a person's name is shown next to the avatar.
   * Otherwise aria-label must be set to make the page accessible.
   *
   *  If aria-label is set, role will internally be set to 'img'.
   */
  'aria-label'?: string;
  /**
   * Specifies the Avatar content.
   */
  children?: ComponentChildren;
};

/**
 * An avatar represents a person or entity as initials or an image.
 */
export function Avatar({ children, src, ...otherProps }: AvatarProps) {
  if (src && !children) {
    return <ImageAvatar src={src} {...otherProps}></ImageAvatar>;
  } else {
    return <ContentAvatar {...otherProps}>{children}</ContentAvatar>;
  }
}

const ImageAvatar = ({ src, ...otherProps }: AvatarProps) => {
  const {
    styles: { base, image }
  } = useComponentTheme<never, AvatarStyles>(AvatarRedwoodTheme);
  const imageClasses = classNames([base, image]);
  return (
    <AvatarWrapper {...otherProps}>
      <div class={imageClasses} style={{ backgroundImage: `url(${src})` }}></div>
    </AvatarWrapper>
  );
};

const ContentAvatar = ({ children, initials, ...otherProps }: AvatarProps) => {
  const {
    styles: { baseStyle, content, initialsStyle, icon }
  } = useComponentTheme<never, AvatarStyles>(AvatarRedwoodTheme);

  const contentClasses = [baseStyle, content, initials ? initialsStyle : icon];

  const childContent = initials || children;
  return (
    <AvatarWrapper {...otherProps}>
      <div aria-hidden="true" class={classNames(contentClasses)}>
        {childContent}
      </div>
    </AvatarWrapper>
  );
};

const AvatarWrapper = ({ background, size, shape, children, ...props }: AvatarProps) => {
  const { classes } = useComponentTheme<AvatarVariantOptions>(AvatarRedwoodTheme, {
    shape,
    size,
    background
  });

  const wrapperClasses = classNames([classes, globalImages]);
  if (props['aria-label']) {
    return (
      <div aria-label={props['aria-label']} role="img" class={wrapperClasses}>
        {children}
      </div>
    );
  } else {
    return <div class={wrapperClasses}>{children}</div>;
  }
};
